import {
  Box,
  Button,
  DialogTitle,
  IconButton,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Stack,
  Typography,
} from "@mui/joy";
import { useEffect, useState } from "react";
import Email from "@mui/icons-material/EmailOutlined";
import WhatsApp from "@mui/icons-material/WhatsApp";
import Phone from "@mui/icons-material/LocalPhone";
import Message from "@mui/icons-material/Message";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  useGenralTeamSettingsQuery,
  useTeamUpdateMutation,
} from "../../../../__generated__/types-and-hooks";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "../../../../i18n";
import Telegram from "@mui/icons-material/Telegram";
import Language from "@mui/icons-material/Language"; // for website
import Business from "@mui/icons-material/Business"; // for LinkedIn
import Instagram from "@mui/icons-material/Instagram"; // optional
import Facebook from "@mui/icons-material/Facebook";
import XIcon from "@mui/icons-material/X"; // for Twitter (now X)

export interface ContactMethodsModalProps {
  open: boolean;
}

const iconMap: Record<string, { icon: React.ReactNode; label: string }> = {
  phone: {
    icon: <Phone fontSize="small" />,
    label: "Phone",
  },
  email: {
    icon: <Email fontSize="small" />,
    label: "Email",
  },
  whatsapp: {
    icon: <WhatsApp fontSize="small" />,
    label: "WhatsApp",
  },
  telegram: {
    icon: <Telegram fontSize="small" />,
    label: "Telegram",
  },
  website: {
    icon: <Language fontSize="small" />,
    label: "Website",
  },
  linkedin: {
    icon: <Business fontSize="small" />,
    label: "LinkedIn",
  },
  instagram: {
    icon: <Instagram fontSize="small" />,
    label: "Instagram",
  },
  facebook: {
    icon: <Facebook fontSize="small" />,
    label: "Facebook",
  },
  twitter: {
    icon: <XIcon fontSize="small" />,
    label: "Twitter",
  },
  other: {
    icon: <Message fontSize="small" />,
    label: "Other",
  },
};

export default function ContactMethodsModal({
  open,
}: ContactMethodsModalProps) {
  const t = useTranslation();
  const navigate = useNavigate();

  const [teamUpdateMutation] = useTeamUpdateMutation({});

  const { teamSlug } = useParams();
  const genralTeamSettingsQuery = useGenralTeamSettingsQuery({
    variables: { slug: teamSlug! },
  });

  const team = genralTeamSettingsQuery.data?.team_by_slug;

  const [contactMethods, setContactMethods] = useState<
    { key: string; value: string }[]
  >([]);

  useEffect(() => {
    if (team) {
      setContactMethods([
        ...team.contacts.map((cm) => ({ key: cm.key, value: cm.value })),
      ]);
    }
  }, [team]);

  if (contactMethods.length === 0) {
    contactMethods.push({ key: "phone", value: "" });
  }
  const handleChange = (index: number, key: string, value: string) => {
    const updatedMethods = [...contactMethods];
    (updatedMethods[index] as any)[key] = value;
    setContactMethods(updatedMethods);
  };
  const handleRemove = (index: number) => {
    const arr = [...contactMethods];
    arr.splice(index, 1);
    setContactMethods(arr);
  };
  const handleAdd = () => {
    const arr = [...contactMethods];
    arr.push({ key: "phone", value: "" });
    setContactMethods(arr);
  };

  const handleSave = async () => {
    if (!team) return;

    teamUpdateMutation({
      variables: {
        team: {
          id: team.id,
          contacts: contactMethods,
        },
      },
      onCompleted() {
        genralTeamSettingsQuery.refetch();
        navigate("../");
      },
    });
  };

  return (
    <Modal open={open} onClose={() => navigate("../")}>
      <ModalDialog size="md" variant="outlined">
        <DialogTitle>
          {team?.name} - {t("Contact Methods")}
        </DialogTitle>
        <ModalClose />
        <Typography
          level="body-xs"
          sx={{ marginTop: 1, wordWrap: "break-word" }}
        >
          {t(
            "Use the dropdown to select the type (e.g., phone, email, WhatsApp) and provide the relevant details."
          )}
          <br />
          {t(
            'Add new methods as needed, or click "Remove" to delete existing ones.'
          )}
        </Typography>
        <Stack spacing={2} sx={{ minHeight: 300 }}>
          {contactMethods.map((method, index) => (
            <Box
              key={index}
              sx={{ display: "flex", alignItems: "center", mb: 2 }}
            >
              <Select
                startDecorator={
                  iconMap[method.key]?.icon ?? <Message fontSize="small" />
                }
                value={method.key}
                onChange={(_e, newValue) =>
                  handleChange(index, "key", newValue!)
                }
                sx={{
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                }}
                renderValue={(option) => (
                  <Typography
                    sx={{
                      textAlign: "left",
                      width: { xs: "auto", sm: 80 },
                      display: { xs: "none", sm: "block" },
                    }}
                  >
                    {option?.label}
                  </Typography>
                )}
              >
                {Object.entries(iconMap).map(([method, { icon, label }]) => (
                  <Option key={method} value={method}>
                    <Typography startDecorator={icon}>{t(label)}</Typography>
                  </Option>
                ))}
              </Select>
              <Input
                type="text"
                value={method.value}
                sx={{
                  flex: 1,
                  borderLeft: 0,
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                }}
                onChange={(e) => handleChange(index, "value", e.target.value)}
                endDecorator={
                  <IconButton color="primary" variant="soft">
                    <ContentCopyIcon
                      onClick={() =>
                        navigator.clipboard.writeText(method.value)
                      }
                    />
                  </IconButton>
                }
              />
              <Button
                color="danger"
                variant="soft"
                onClick={() => handleRemove(index)}
                startDecorator={<DeleteOutlineOutlinedIcon />}
                sx={{
                  ml: 2,
                  display: { xs: "none", sm: "flex" },
                }}
              >
                {t("Remove")}
              </Button>
              <IconButton
                color="danger"
                variant="soft"
                onClick={() => handleRemove(index)}
                sx={{
                  ml: 2,
                  display: { xs: "flex", sm: "none" },
                }}
              >
                <DeleteOutlineOutlinedIcon />
              </IconButton>
            </Box>
          ))}
          <Box>
            <Button
              variant="soft"
              onClick={handleAdd}
              startDecorator={<AddIcon />}
            >
              {t("Add")}
            </Button>
          </Box>
        </Stack>
        <Button type="submit" onClick={handleSave}>
          {t("Save")}
        </Button>
      </ModalDialog>
    </Modal>
  );
}
